import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import type { ManualSection } from "@/content/manualTypes";
import { ManualSectionBlock } from "@/components/manual/ManualSectionBlock";
import ManualGate from "@/components/manual/ManualGate";
import { smoothScrollTo } from "@/lib/smoothScroll";
import { cn } from "@/lib/utils";

type NavItem = { id: string; navLabel: string };

const SCROLL_OFFSET = 96;

const InstallationManual = () => {
  const [sections, setSections] = useState<ManualSection[] | null>(null);
  const [started, setStarted] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const stepRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const unlocked = sections !== null;
  const manualSections = useMemo(() => sections ?? [], [sections]);
  const navItems: NavItem[] = useMemo(
    () => [
      ...manualSections.map(({ id, navLabel }) => ({ id, navLabel })),
      { id: "contact", navLabel: "Contact" },
    ],
    [manualSections],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 1023px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const unlock = (loaded: ManualSection[]) => {
    setSections(loaded);
    setActiveId(loaded[0]?.id ?? "contact");
  };




  // Desktop: scroll spy + progress bar
  useEffect(() => {
    if (!unlocked || isMobile) return;

    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(scrollable > 0 ? Math.min(1, window.scrollY / scrollable) : 0);

      const offset = window.scrollY + SCROLL_OFFSET + 24;
      let current = navItems[0]?.id ?? "contact";
      for (const section of navItems) {
        const el = sectionRefs.current[section.id];
        if (el && el.offsetTop <= offset) current = section.id;
      }
      setActiveId(current);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [unlocked, isMobile, navItems]);

  const activeIndex = navItems.findIndex((item) => item.id === activeId);

  // Mobile: keep the active step chip in view + progress reflects step position
  useEffect(() => {
    if (!isMobile || !unlocked) return;
    setProgress(navItems.length > 1 ? (activeIndex + 1) / navItems.length : 1);
    stepRefs.current[activeId]?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [isMobile, unlocked, activeId, activeIndex]);

  const jumpTo = (id: string) => {
    if (isMobile) {
      setActiveId(id);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const el = sectionRefs.current[id];
    if (!el) return;
    smoothScrollTo(Math.max(0, el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET), 650);
  };

  const head = useMemo(
    () => (
      <Helmet>
        <title>Installation manual — Cetasol installation portal</title>
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
        <meta name="googlebot" content="noindex, nofollow" />
        <meta name="description" content="Private Cetasol installation portal." />
      </Helmet>
    ),
    [],
  );

  const contactSection = (
    <section
      id="contact"
      ref={(el) => {
        sectionRefs.current.contact = el;
      }}
      className="scroll-mt-28 py-12 sm:py-16"
    >
      <div className="rounded-3xl border border-border bg-card/50 p-8 sm:p-12">
        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
          Thank you for using the Cetasol system
        </h2>
        <p className="mt-4 max-w-[60ch] leading-relaxed text-foreground/70">
          For any questions or support during installation, contact the responsible installation
          partner. For everything else, our team is one message away.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <a
            href="mailto:info@cetasol.com"
            className="inline-flex items-center rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-[hsl(var(--primary-hover))]"
          >
            info@cetasol.com
          </a>
          <a
            href="https://cetasol.com/contact"
            className="inline-flex items-center rounded-lg border border-border px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-primary"
          >
            Contact Cetasol
          </a>
        </div>
      </div>
    </section>
  );

  if (!unlocked) {
    return (
      <div className="manual-theme min-h-screen bg-background font-body text-foreground">
        {head}
        <ManualGate onUnlock={unlock} />
      </div>
    );
  }

  if (!started) {
    return (
      <div className="manual-theme min-h-screen bg-background font-body text-foreground">
        {head}
        <div className="relative overflow-hidden">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl"
          />
          <div className="relative mx-auto max-w-3xl px-5 py-16 sm:px-6 sm:py-24">
            <p className="text-xs uppercase tracking-[0.3em] text-primary">Cetasol installation portal</p>
            <h1 className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
              Welcome
            </h1>
            <p className="mt-6 max-w-[62ch] text-lg leading-relaxed text-foreground/70">
              This guide takes you through installing the Cetasol Main Unit and the iHelm captain display
              on board, one step at a time. Follow the steps in order — each step shows what to connect,
              with photos of every port and cable.
            </p>

            <ul className="mt-10 space-y-3 border-t border-border pt-8 text-sm text-foreground/70">
              <li className="flex gap-3">
                <span className="font-mono text-primary">01</span>
                Start with the system overview to see how the parts fit together
              </li>
              <li className="flex gap-3">
                <span className="font-mono text-primary">02</span>
                Check the kit contents before you begin
              </li>
              <li className="flex gap-3">
                <span className="font-mono text-primary">03</span>
                Work through the steps and finish with the power-up check
              </li>
            </ul>

            <button
              type="button"
              onClick={() => {
                setStarted(true);
                setActiveId(manualSections[0]?.id ?? "contact");
                window.scrollTo({ top: 0 });
              }}
              className="mt-10 inline-flex items-center gap-2 rounded-lg bg-primary px-7 py-4 text-base font-semibold text-primary-foreground transition-colors hover:bg-[hsl(var(--primary-hover))]"
            >
              Get started
              <ArrowRight className="h-4 w-4" />
            </button>

            <p className="mt-6 text-sm text-muted-foreground">
              {manualSections.length} steps · document version v1.1 · for customers and installation partners
            </p>
          </div>
        </div>
      </div>
    );
  }


  const activeSectionIndex = manualSections.findIndex((section) => section.id === activeId);
  const activeSection = activeSectionIndex >= 0 ? manualSections[activeSectionIndex] : null;

  return (
    <div className="manual-theme min-h-screen bg-background font-body text-foreground">
      {head}

      <div
        aria-hidden
        className="fixed inset-x-0 top-0 z-50 h-1 origin-left bg-primary transition-transform duration-150"
        style={{ transform: `scaleX(${progress})` }}
      />

      {/* Mobile step navigation */}
      <div className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur lg:hidden">
        <div className="flex items-center justify-between px-4 pb-2 pt-4">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Steps</span>
          <span className="font-mono text-xs text-primary">
            {activeIndex + 1}/{navItems.length}
          </span>
        </div>
        <nav
          aria-label="Manual steps"
          className="flex gap-2 overflow-x-auto px-4 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {navItems.map((section, index) => {
            const isActive = section.id === activeId;
            const isContact = section.id === "contact";
            return (
              <button
                key={section.id}
                type="button"
                ref={(el) => {
                  stepRefs.current[section.id] = el;
                }}
                onClick={() => jumpTo(section.id)}
                aria-current={isActive ? "true" : undefined}
                aria-label={`${isContact ? "Contact" : `Step ${index + 1}`}: ${section.navLabel}`}
                className={cn(
                  "shrink-0 rounded-full border px-4 py-2 font-mono text-sm transition-colors",
                  isActive
                    ? "border-primary bg-primary/15 font-semibold text-primary"
                    : "border-border text-foreground/60",
                )}
              >
                {isContact ? "Contact" : index + 1}
              </button>
            );
          })}
        </nav>
        <p className="truncate border-t border-border/60 px-4 py-2 text-sm font-semibold">
          {navItems[activeIndex]?.navLabel}
        </p>
      </div>

      {!isMobile && (
        <header className="relative overflow-hidden border-b border-border">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl"
          />
          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
            <p className="text-xs uppercase tracking-[0.3em] text-primary">Cetasol installation portal</p>
            <h1 className="mt-5 max-w-3xl text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
              Installation manual
            </h1>
            <p className="mt-6 max-w-[62ch] text-lg leading-relaxed text-foreground/70">
              Everything needed to install the Cetasol Main Unit and the iHelm captain display on board.
              Use the contents list to jump straight to the step you are working on.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-4 border-t border-border pt-8 text-sm text-muted-foreground">
              <span>
                <span className="mr-2 font-mono text-primary">{manualSections.length}</span>steps
              </span>
              <span>
                <span className="mr-2 font-mono text-primary">v1.1</span>document version
              </span>
              <span>For customers and installation partners</span>
            </div>
          </div>
        </header>
      )}

      <div className="mx-auto flex max-w-7xl gap-12 px-4 sm:px-6 lg:px-8">
        <aside className="hidden w-64 shrink-0 lg:block">
          <nav
            aria-label="Table of contents"
            className="sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto py-16 pr-2"
          >
            <p className="mb-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">Contents</p>
            <ul className="space-y-1 border-l border-border">
              {navItems.map((section, index) => {
                const isActive = section.id === activeId;
                const isContact = section.id === "contact";
                return (
                  <li key={section.id}>
                    <button
                      type="button"
                      onClick={() => jumpTo(section.id)}
                      aria-current={isActive ? "true" : undefined}
                      className={cn(
                        "-ml-px block w-full border-l-2 py-2 pl-4 text-left text-sm transition-colors",
                        isActive
                          ? "border-primary font-semibold text-primary"
                          : "border-transparent text-foreground/60 hover:border-border hover:text-foreground",
                      )}
                    >
                      {!isContact && (
                        <span className="mr-2 font-mono text-xs text-muted-foreground">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                      )}
                      {section.navLabel}
                    </button>
                  </li>
                );
              })}

            </ul>
          </nav>
        </aside>

        <main className="min-w-0 flex-1 pb-8">
          {isMobile ? (
            <>
              {activeSection ? (
                <ManualSectionBlock
                  key={activeSection.id}
                  section={activeSection}
                  index={activeSectionIndex}
                  ref={(el) => {
                    sectionRefs.current[activeSection.id] = el;
                  }}
                />
              ) : (
                contactSection
              )}

              <div className="flex items-center justify-between gap-3 border-t border-border py-6">
                <button
                  type="button"
                  disabled={activeIndex <= 0}
                  onClick={() => jumpTo(navItems[activeIndex - 1].id)}
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-3 text-sm font-semibold transition-colors disabled:opacity-30"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <button
                  type="button"
                  disabled={activeIndex >= navItems.length - 1}
                  onClick={() => jumpTo(navItems[activeIndex + 1].id)}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground transition-colors disabled:opacity-30"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </>
          ) : (
            <>
              {manualSections.map((section, index) => (
                <ManualSectionBlock
                  key={section.id}
                  section={section}
                  index={index}
                  ref={(el) => {
                    sectionRefs.current[section.id] = el;
                  }}
                />
              ))}
              {contactSection}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default InstallationManual;
